// src/stores/uiStore.ts
import { create } from 'zustand';

/**
 * Shape of a support ticket row as stored in the UI store.
 * Kept permissive (JSONB-style) — the full row shape lives in Supabase.
 */
export type UserTicket = Record<string, unknown>;

export interface ChatEntityRef {
  type: string;
  id: string;
  label?: string;
  subtitle?: string;
  details?: string;
  status?: string;
  dates?: string;
  room?: string;
  property?: string;
  priority?: string;
  dueDate?: string;
  totalPrice?: number | string;
  totalAmount?: number | string;
  meta?: Record<string, any>;
  [key: string]: any;
}

// The nav-group expansion map (consumed by AppNav) is persisted to localStorage
// under this key. It is initialized SYNCHRONOUSLY via loadExpandedGroups()
// instead of a post-mount effect: under React StrictMode the restore effect
// runs twice with stale initial-closures and the persistence effect then wrote
// {} over the restored value on every reload (the reload-forgets-collapse bug).
export const EXPANDED_GROUPS_STORAGE_KEY = "adapthub_expanded_groups";
export const THEME_STORAGE_KEY = "adapthub_theme";

export type ThemeMode = 'light' | 'dark';

const loadTheme = (): ThemeMode => {
  if (typeof window === "undefined") return 'light';
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === 'dark' || saved === 'light') {
      document.documentElement.classList.toggle('dark', saved === 'dark');
      return saved;
    }
  } catch {
    // Storage unavailable or blocked — fallback to light mode
  }
  return 'light';
};

const loadExpandedGroups = (): Record<string, boolean> => {
  if (typeof window === "undefined") return {};
  try {
    const saved = localStorage.getItem(EXPANDED_GROUPS_STORAGE_KEY);
    if (!saved) return {};
    const parsed: unknown = JSON.parse(saved);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, boolean>;
    }
    // Not an object (e.g. the literal string "undefined" from a prior crash
    // path): drop it so the next write starts clean. Never crash the shell.
    localStorage.removeItem(EXPANDED_GROUPS_STORAGE_KEY);
  } catch {
    // Unparseable JSON: same cleanup path. Storage being unavailable must
    // never take down the app.
    try {
      localStorage.removeItem(EXPANDED_GROUPS_STORAGE_KEY);
    } catch {
      // ignore — nothing to clean
    }
  }
  return {};
};

/**
 * Global UI state store (Zustand).
 *
 * Replaces ~15 useState calls in AuthedApp.jsx for UI-only state
 * (modals, drawers, sidebar, support tickets UI state).
 *
 * Server state (reservations, data) is handled by TanStack Query.
 * Filter state (date filters, search) is in filtersStore.ts.
 *
 * Usage:
 *   const isHistoryOpen = useUIStore(s => s.isHistoryOpen);
 *   const setHistoryOpen = useUIStore(s => s.setHistoryOpen);
 */
export interface UIState {
  // ── Sidebar & mobile menu ──────────────────────────────────────
  isMobileMenuOpen: boolean;
  isSidebarCollapsed: boolean;

  // ── Modals & drawers ───────────────────────────────────────────
  isHistoryOpen: boolean;
  isPatchNotesOpen: boolean;
  hasNewPatchNote: boolean;
  isSupportOpen: boolean;
  isPreferencesOpen: boolean;
  globalSupportTicketIdToOpen: string | null;

  // ── Support ticket UI state ────────────────────────────────────
  hasUnreadSupportTickets: boolean;
  hasUnreadUserReplies: boolean;
  userTickets: UserTicket[];
  readUpdateTrigger: number;

  // ── Internal messenger (standalone chat) ───────────────────────
  // NOTE: the unread COUNT deliberately does NOT live here. AuthedApp derives
  // it from `useChatUnread`, which shares the TanStack Query cache with the
  // open ChatView (queryKeys.messenger.conversations). A store copy would be a
  // second source of truth that can drift from the query cache.
  // Share-to-chat (phase-2): an entity view hands a ref to the messenger —
  // "send to chat" in TraceDetail/OfferDetail opens /chat with the entity
  // pre-attached; ChatView consumes + clears it after stamping data.entity_ref.
  chatEntityRef: ChatEntityRef | null;

  // ── In-app file viewer (attachments) ──────────────────────────
  // Global "open attachment" target: any openStorageFile() call (chat,
  // traces, belege, contracts, support tickets, kassenbuch …) routes the
  // storage ref here instead of a new browser tab. FileViewerModal — mounted
  // once in AuthedApp — consumes it, resolves a fresh signed URL and renders
  // the file inline (img/video/audio/pdf/text; download fallback otherwise).
  // `name` is the human display name (file name) when the caller knows it.
  fileViewer: { ref: string; name?: string } | null;

  // ── Dashboard ──────────────────────────────────────────────────
  expandedGroups: Record<string, boolean>;

  // ── Theme ──────────────────────────────────────────────────────
  theme: ThemeMode;

  // ── Actions ────────────────────────────────────────────────────
  setMobileMenuOpen: (v: boolean) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (v: boolean) => void;
  setHistoryOpen: (v: boolean) => void;
  setPatchNotesOpen: (v: boolean) => void;
  setHasNewPatchNote: (v: boolean) => void;
  setSupportOpen: (v: boolean) => void;
  setPreferencesOpen: (v: boolean) => void;
  setGlobalSupportTicketIdToOpen: (v: string | null) => void;
  setHasUnreadSupportTickets: (v: boolean) => void;
  setHasUnreadUserReplies: (v: boolean) => void;
  setUserTickets: (v: UserTicket[]) => void;
  incrementReadUpdateTrigger: () => void;
  setExpandedGroups: (
    v:
      | Record<string, boolean>
      | ((prev: Record<string, boolean>) => Record<string, boolean>),
  ) => void;
  toggleExpandedGroup: (key: string) => void;
  setChatEntityRef: (v: ChatEntityRef | null) => void;
  openFileViewer: (ref: string, name?: string) => void;
  closeFileViewer: () => void;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
}

export const useUIStore = create<UIState>()((set) => ({
  // ── Sidebar & mobile menu ──────────────────────────────────────
  isMobileMenuOpen: false,
  isSidebarCollapsed: false,

  // ── Modals & drawers ───────────────────────────────────────────
  isHistoryOpen: false,
  isPatchNotesOpen: false,
  hasNewPatchNote: false,
  isSupportOpen: false,
  isPreferencesOpen: false,
  globalSupportTicketIdToOpen: null,

  // ── Support ticket UI state ────────────────────────────────────
  hasUnreadSupportTickets: false,
  hasUnreadUserReplies: false,
  userTickets: [],
  readUpdateTrigger: 0,

  // ── Internal messenger (standalone chat) ───────────────────────
  chatEntityRef: null,

  // ── In-app file viewer (attachments) ──────────────────────────
  fileViewer: null,

  // ── Dashboard ──────────────────────────────────────────────────
  // Restored synchronously from localStorage (see loadExpandedGroups above) —
  // NOT via a mount effect, so StrictMode's double-mount cannot clobber it.
  expandedGroups: loadExpandedGroups(),

  // ── Theme ──────────────────────────────────────────────────────
  theme: loadTheme(),

  // ── Actions ────────────────────────────────────────────────────
  setMobileMenuOpen: (v) => set({ isMobileMenuOpen: v }),
  toggleSidebar: () => set((s) => ({ isSidebarCollapsed: !s.isSidebarCollapsed })),
  setSidebarCollapsed: (v) => set({ isSidebarCollapsed: v }),
  setHistoryOpen: (v) => set({ isHistoryOpen: v }),
  setPatchNotesOpen: (v) => set({ isPatchNotesOpen: v }),
  setHasNewPatchNote: (v) => set({ hasNewPatchNote: v }),
  setSupportOpen: (v) => set({ isSupportOpen: v }),
  setPreferencesOpen: (v) => set({ isPreferencesOpen: v }),
  setGlobalSupportTicketIdToOpen: (v) => set({ globalSupportTicketIdToOpen: v }),
  setHasUnreadSupportTickets: (v) => set({ hasUnreadSupportTickets: v }),
  setHasUnreadUserReplies: (v) => set({ hasUnreadUserReplies: v }),
  setUserTickets: (v) => set({ userTickets: v }),
  incrementReadUpdateTrigger: () => set((s) => ({ readUpdateTrigger: s.readUpdateTrigger + 1 })),
  // Accepts either a plain map or an updater function. The updater form MUST be
  // handled here: `set({ expandedGroups: v })` stores the function object
  // verbatim (Zustand only invokes a function when the set() argument itself is
  // a function), which made Object.entries(expandedGroups) return [] and every
  // nav group render as permanently expanded.
  setExpandedGroups: (v) =>
    set((s) => ({
      expandedGroups:
        typeof v === "function" ? v(s.expandedGroups || {}) : v,
    })),
  setChatEntityRef: (v) => set({ chatEntityRef: v }),
  // The same ref opened twice in a row must still re-render the viewer (e.g.
  // the user closed it and clicks the same attachment again). Storing a fresh
  // object identity per open guarantees the consumer's useEffect re-runs even
  // though the ref string is identical.
  openFileViewer: (ref, name) => set({ fileViewer: { ref, name } }),
  closeFileViewer: () => set({ fileViewer: null }),
  toggleExpandedGroup: (key) => set((s) => ({
    expandedGroups: { ...s.expandedGroups, [key]: !s.expandedGroups[key] },
  })),
  setTheme: (theme: ThemeMode) => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
      document.documentElement.classList.toggle('dark', theme === 'dark');
    } catch {
      // Storage unavailable — ignore
    }
    set({ theme });
  },
  toggleTheme: () =>
    set((s) => {
      const next: ThemeMode = s.theme === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(THEME_STORAGE_KEY, next);
        document.documentElement.classList.toggle('dark', next === 'dark');
      } catch {
        // Storage unavailable — ignore
      }
      return { theme: next };
    }),
}));
